import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Award, Clock } from "lucide-react";
import * as LucideIcons from "lucide-react";
import Navbar from "../../../sub-comp/Navbar";
import FullScreenImage from "../../../Home/ShowcaseSection";
import Footer from "../../../sub-comp/MainFooter";
import { getImageUrl } from "../../../../utils/getImageUrl";

const CustomAdobeTemplate = ({ programData }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [visibleCards, setVisibleCards] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 200);

    // Animate cards in sequence
    const customContent = programData?.customContent 
      ? (typeof programData.customContent === 'string' 
          ? JSON.parse(programData.customContent) 
          : programData.customContent)
      : null;
    
    if (customContent?.applications) {
      const cardTimer = setTimeout(() => {
        customContent.applications.forEach((_, index) => {
          setTimeout(() => {
            setVisibleCards((prev) => [...prev, index]);
          }, index * 200);
        });
      }, 800);

      return () => {
        clearTimeout(timer);
        clearTimeout(cardTimer);
      };
    }

    return () => {
      clearTimeout(timer);
    };
  }, [programData]);

  // Parse new custom layout fields from database
  const heroApplications = programData?.heroApplications
    ? (typeof programData.heroApplications === 'string'
        ? JSON.parse(programData.heroApplications)
        : programData.heroApplications)
    : [];

  const applicationCards = programData?.applicationCards
    ? (typeof programData.applicationCards === 'string'
        ? JSON.parse(programData.applicationCards)
        : programData.applicationCards)
    : [];

  const learningLevels = programData?.learningLevels
    ? (typeof programData.learningLevels === 'string'
        ? JSON.parse(programData.learningLevels)
        : programData.learningLevels)
    : [];

  // Legacy support: fallback to customContent if new fields are empty
  const customContent = programData?.customContent 
    ? (typeof programData.customContent === 'string' 
        ? JSON.parse(programData.customContent) 
        : programData.customContent)
    : { applications: [], skillLevels: [] };

  // Use new fields if available, otherwise fallback to legacy
  const applications = applicationCards.length > 0 ? applicationCards : (customContent.applications || []);
  const skillLevels = learningLevels.length > 0 ? learningLevels : (customContent.skillLevels || []);
  
  // Get suite section info
  const suiteTitle = programData?.suiteTitle || 'Complete Adobe Creative Suite';
  const suiteDescription = programData?.suiteDescription || 'Learn industry-leading creative tools used by professionals worldwide';
  
  // Get learning path section info
  const learningPathTitle = programData?.learningPathTitle || 'Learning Path';
  const learningPathDesc = programData?.learningPathDesc || 'Progress from beginner to advanced with our structured curriculum';

  // Helper function to get icon component with color support
  const getIconComponent = (iconName, color = null) => {
    const IconComponent = LucideIcons[iconName] || LucideIcons.Image;
    // Map common color names to Tailwind classes
    const colorMap = {
      'blue': 'text-blue-600',
      'red': 'text-red-600',
      'green': 'text-green-600',
      'purple': 'text-purple-600',
      'orange': 'text-orange-600',
      'yellow': 'text-yellow-400',
      'yellow-400': 'text-yellow-400',
      'yellow-500': 'text-yellow-500',
      'pink': 'text-pink-600',
      'indigo': 'text-indigo-600',
      'custom-pink': 'text-custom-pink',
      'white': 'text-white',
      'black': 'text-black',
    };
    // Handle color - if null, no color class; if specified, use colorMap or construct class
    let colorClass = '';
    if (color) {
      if (colorMap[color]) {
        colorClass = colorMap[color];
      } else if (color.startsWith('text-')) {
        colorClass = color;
      } else {
        // Try to construct Tailwind class
        colorClass = `text-${color}-600`;
      }
    }
    return <IconComponent className={`w-12 h-12 ${colorClass}`} />;
  };

  // Default hero image - use utility to handle different path types
  const heroImage = getImageUrl(programData?.heroImage || '/assets/Adobe.jpeg');

  // Parse hero title (split by newline for two-line display)
  const heroTitleParts = programData?.heroTitle?.split('\n') || [programData?.heroTitle || 'Adobe Creative Cloud Suite'];

  return (
    <div>
      <Navbar />
      <div className="mid-desktop-page min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
        {/* Hero Section with Split Design */}
        <div className="relative h-screen flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroImage}
              alt={programData?.heroTitle || 'Adobe Applications'}
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
              onError={(e) => {
                e.target.src = getImageUrl('/assets/Adobe.jpeg');
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-purple-900/90 via-pink-900/80 to-red-900/90"></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div
              className={`text-white transform transition-all duration-1000 ${
                isVisible
                  ? "translate-x-0 opacity-100"
                  : "-translate-x-full opacity-0"
              }`}
            >
              <div className="mb-6">
                <Link
                  to="/course/information-technology"
                  className="flex items-center text-white/80 hover:text-white font-medium transition-all duration-300 hover:translate-x-1 group mb-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2 transition-transform duration-300 group-hover:-translate-x-1" />
                  Back to IT Programs
                </Link>
              </div>
              <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                {heroTitleParts[0]}
                {heroTitleParts[1] && (
                  <span className="block text-yellow-400">{heroTitleParts[1]}</span>
                )}
              </h1>
              <p className="text-xl mb-8 text-white/90">
                {programData?.heroSubtitle || 'Master professional design, video editing, and multimedia creation'}
              </p>
            </div>

            {/* Right Content - Floating Cards */}
            <div
              className={`transform transition-all duration-1000 delay-500 ${
                isVisible
                  ? "translate-x-0 opacity-100"
                  : "translate-x-full opacity-0"
              }`}
            >
              <div className="grid grid-cols-2 gap-4">
                {(heroApplications.length > 0 ? heroApplications : applications.slice(0, 4)).map((app, index) => {
                  // Get color for hero icon - default to white if not specified
                  const heroIconColor = app.color || (heroApplications.length > 0 ? 'white' : null);
                  return (
                    <div
                      key={index}
                      className={`bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 ${
                        index % 2 === 0 ? "animate-bounce" : "animate-pulse"
                      }`}
                      style={{
                        animationDelay: `${index * 0.5}s`,
                        animationDuration: "3s",
                      }}
                    >
                      {getIconComponent(app.icon, heroIconColor)}
                      <h3 className="text-white font-semibold mt-2">
                        {app.name}
                      </h3>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Adobe Applications Grid - Always show section */}
        <section className="py-20 px-4">
            <div className="max-w-7xl mx-auto">
              <div
                className={`text-center mb-16 transform transition-all duration-800 ${
                  isVisible
                    ? "translate-y-0 opacity-100"
                    : "translate-y-8 opacity-0"
                }`}
              >
                <h2 className="text-4xl font-bold text-gray-800 mb-4">
                  {suiteTitle}
                </h2>
                <p className="text-xl text-gray-600">
                  {suiteDescription}
                </p>
              </div>

              {applications.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
                  {applications.map((app, index) => {
                  const colorClass = app.color || 'purple';
                  return (
                    <div
                      key={index}
                      className={`bg-white rounded-2xl shadow-lg hover:shadow-2xl p-6 transition-all duration-500 hover:-translate-y-4 hover:rotate-1 group cursor-pointer border-l-4 transform ${
                        visibleCards.includes(index)
                          ? "translate-y-0 opacity-100 scale-100"
                          : "translate-y-12 opacity-0 scale-90"
                      }`}
                      style={{ borderLeftColor: `var(--color-${colorClass}-500)` }}
                    >
                      <div className="flex items-center mb-4">
                        <div className="transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                          {getIconComponent(app.icon, app.color)}
                        </div>
                        <div className="ml-4">
                          <h3 className="font-bold text-gray-800 group-hover:text-custom-pink transition-colors duration-300">
                            {app.name}
                          </h3>
                        </div>
                      </div>
                      <p className="text-gray-600 mb-4 text-sm">
                        {app.description}
                      </p>
                      <div className="space-y-2">
                        {app.features?.map((feature, featureIndex) => (
                          <div
                            key={featureIndex}
                            className="flex items-center text-sm text-gray-500 transform transition-all duration-300 group-hover:translate-x-2 group-hover:text-custom-pink"
                          >
                            <div className="w-2 h-2 bg-custom-pink rounded-full mr-2"></div>
                            {feature}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">No applications added yet. Applications will appear here once added in the dashboard.</p>
                </div>
              )}
            </div>
          </section>

        {/* Skill Levels Section - Always show section */}
        <section className="py-20 px-4 bg-gradient-to-r from-purple-50/50 to-pink-50/50">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-800 mb-4">
                  {learningPathTitle}
                </h2>
                <p className="text-xl text-gray-600">
                  {learningPathDesc}
                </p>
              </div>

              {skillLevels.length > 0 ? (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
                  {skillLevels.map((level, index) => {
                    // Support both new format (level, duration, modules) and legacy format (title, duration, apps)
                    const levelTitle = level.level || level.title || '';
                    const levelDuration = level.duration || '';
                    const levelModules = level.modules || level.apps || [];
                    
                    return (
                      <div
                        key={index}
                        className={`bg-white rounded-2xl shadow-lg p-8 text-center hover:shadow-xl transition-all duration-500 hover:-translate-y-2 group transform ${
                          isVisible
                            ? "translate-y-0 opacity-100"
                            : "translate-y-8 opacity-0"
                        }`}
                        style={{ transitionDelay: `${index * 200}ms` }}
                      >
                        <div className="mb-6 flex justify-center">
                          <div className="bg-custom-pink/10 p-4 rounded-full group-hover:bg-custom-pink/20 transition-colors duration-300">
                            <Award className="w-6 h-6 text-custom-pink" />
                          </div>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-2 group-hover:text-custom-pink transition-colors duration-300">
                          {levelTitle}
                        </h3>
                        <div className="flex items-center justify-center mb-4 text-gray-600">
                          <Clock className="w-4 h-4 mr-2" />
                          <span>{levelDuration}</span>
                        </div>
                        <div className="space-y-2">
                          {levelModules.map((module, moduleIndex) => (
                            <div
                              key={moduleIndex}
                              className="text-gray-600 text-sm bg-gray-50 py-2 px-3 rounded-lg hover:bg-custom-pink/5 transition-colors duration-300"
                            >
                              {module}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-lg">
                  <p className="text-gray-500">No skill levels added yet. Learning path will appear here once added in the dashboard.</p>
                </div>
              )}
            </div>
          </section>

        {/* Call to Action */}
        {(programData?.ctaTitle || programData?.ctaDescription) && (
          <section className="py-20 px-4 bg-gradient-to-r from-purple-700 to-pink-700">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h2 className="text-4xl font-bold mb-6">
                {programData.ctaTitle || 'Ready to Master Adobe?'}
              </h2>
              <p className="text-xl mb-8 opacity-90">
                {programData.ctaDescription || 'Join thousands of students who have enhanced their creative skills'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/admissions" className="block text-center bg-yellow-500 text-black px-8 py-4 rounded-full font-semibold text-lg hover:bg-yellow-400 transition-all duration-300 transform hover:scale-105 hover:shadow-lg">
                  Start Learning Today
                </Link>
                <Link to="/contact" className="block text-center border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-purple-700 transition-all duration-300 transform hover:scale-105">
                  Download Brochure
                </Link>
              </div>
            </div>
          </section>
        )}
      </div>
      <FullScreenImage />
      <Footer />
    </div>
  );
};

export default CustomAdobeTemplate;


